import { useSelector } from 'react-redux';
import { LeaseDetailsModel, LeaseSignatureInfo } from 'api/models';
import { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import ResidentTileList from 'containercomponents/ResidentTileList/ResidentTileList';
import ResidentTileListWithStatus from 'containercomponents/ResidentTileListWithStatus/ResidentTileListWithStatus';
import { ResidentProfileProps } from 'containercomponents/ResidentTileWithStatus/ResidentTileWithStatus';
import { differenceInCalendarYears } from 'date-fns';
import { flatten } from 'es-toolkit/compat';
import { legacyParse } from 'forkedlibraries/date-fns-upgrade';
import { ResidentType } from 'models/enums';
import { selectUser } from 'store/redux/user/selectors';

const getPetIcon = (type: string) => {
  if (type === 'Dog') {
    return GENERAL_ICONS.DOG_PROFILE;
  } else if (type === 'Cat') {
    return GENERAL_ICONS.CAT_PROFILE;
  }

  return GENERAL_ICONS.OTHER_PET_PROFILE;
};

type ResidentListContainerProps = {
  // The three lists this reads off a lease, named from the model rather than as
  // a `Record<string, unknown>` — destructuring that gave `{}`, which has no
  // `map`.
  residents: Pick<LeaseDetailsModel, 'residents' | 'kids' | 'pets'>;
  withStatus?: boolean;
  // TODO: add orientation to ResidentTileList if needed (now is only implemented for ResidentTileListWithStatus)
  orientation?: 'horizontal' | 'vertical';
  // The lease's own signature info, which is what `TourAndAppsCard` passes —
  // this used to name that card's exported wrapper type (`{ leaseId,
  // signatureInfo }`) rather than the `signatureInfo` it actually hands over.
  signatureInformation?: LeaseSignatureInfo;
};

const ResidentListContainer = ({
  residents,
  withStatus = false,
  orientation = 'horizontal',
  signatureInformation,
}: ResidentListContainerProps) => {
  const currentUser = useSelector(selectUser);
  const {
    residents: residentsList = [],
    kids = [],
    pets = [],
  } = residents || {};
  const residentsProps = residentsList?.map((resident) => {
    // `basicInfo` and `userInfo` are optional on the model but both reads were
    // already unconditional; the assertions keep that throw rather than turning
    // it into a silently-skipped tile.
    const { basicInfo, leaseInfo } = resident;
    const { userInfo } = basicInfo!;

    let residentSubtitle;
    switch (leaseInfo?.residentType) {
      case ResidentType.Primary:
        residentSubtitle = 'Primary Resident';
        break;
      case ResidentType.CoSigner:
        residentSubtitle = 'Co-signer';
        break;
      case ResidentType.Roommate:
      default:
        residentSubtitle = 'Resident';
        break;
    }
    return {
      profileProps: {
        imageUrl: userInfo!.profileImageUrl,
        title:
          userInfo!.userId === currentUser!.userId
            ? 'Me'
            : `${userInfo!.firstName} ${userInfo!.lastName}`,
        subtitles: [residentSubtitle],
        userId: userInfo!.userId,
        signatureStatus: leaseInfo?.signatureStatus,
      },
    };
  });

  const kidProps = kids?.map((kid) => {
    const birthdate = new Date(kid.dateOfBirth);
    const childAgeInYears = differenceInCalendarYears(
      legacyParse(Date.now()),
      legacyParse(birthdate),
    );
    return {
      profileProps: {
        title: kid.name,
        subtitles: [
          childAgeInYears > 0
            ? `${childAgeInYears} Year${childAgeInYears === 1 ? '' : 's'} Old`
            : 'Baby',
        ],
        customEmptyProfileIcon: GENERAL_ICONS.KIDS_PROFILE,
        // signatureStatus: 'Signature Not Required',
      },
    };
  });

  const petProps = pets?.map((pet) => ({
    profileProps: {
      title: pet.name,
      subtitles: [`${pet.subType || ''}`],
      customEmptyProfileIcon: getPetIcon(pet.petType),
      // signatureStatus: 'Signature Not Required',
    },
  }));

  const getInfo = [...residentsProps, ...kidProps, ...petProps];
  // Annotated because the array is seeded empty and only ever written to inside
  // the callback below, which is too late for `tsc` to infer an element type.
  const newArrayOfResidents: { profileProps: ResidentProfileProps }[][] = [];
  getInfo.forEach((value, index) => {
    const number = (index - 1) / 2;

    if (!newArrayOfResidents[Math.round(number)]) {
      newArrayOfResidents[Math.round(number)] = [];
    }

    newArrayOfResidents[Math.round(number)]!.push(value);
  });

  return withStatus ? (
    // Will never understand why we gave residents the shape we are giving it right now
    // But will flatten out since this component is be new.
    <ResidentTileListWithStatus
      residents={flatten(newArrayOfResidents)}
      orientation={orientation}
      signatureInformation={signatureInformation}
    />
  ) : (
    <ResidentTileList residents={newArrayOfResidents} />
  );
};

export default ResidentListContainer;
